/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import { assign, forEach, set } from 'lodash';
import { getSuperAdminData } from './helpers/super-admin';
import * as Cognito from './api/cognito';

// Maps to store active request controllers
const activeRequests = new Map();
const activeRequestGroups = new Map();

/**
 * Adds an AbortController to a request group for batch cancellation
 * @param {string} groupKey - The group identifier
 * @param {AbortController} controller - The controller to add
 */
function addAbortControllerToGroup(groupKey, controller) {
  if (!groupKey) return;
  if (!activeRequestGroups.has(groupKey)) {
    activeRequestGroups.set(groupKey, new Set());
  }
  activeRequestGroups.get(groupKey).add(controller);
}

/**
 * Cancels all requests in a group
 * @param {string} groupKey - The group identifier
 */
export function cancelGroupRequests(groupKey) {
  if (!groupKey) return;
  const controllers = activeRequestGroups.get(groupKey);
  if (controllers) {
    forEach(controllers, (controller) => controller.abort());
    activeRequestGroups.delete(groupKey);
  }
}

// Create axios instance with default config
const client = axios.create({
  timeout: 40000,
  timeoutErrorMessage: 'Request timed out',
});

/**
 * Handle request lifecycle: authentication, cancellation, and super admin features
 */
client.interceptors.request.use(async (config) => {
  const { requestTrackerId, cancelGroup } = config;

  // Handle request cancellation
  if (requestTrackerId) {
    // Cancel previous request with same ID if exists
    const previousController = activeRequests.get(requestTrackerId);
    if (previousController) {
      // Abort previous request
      previousController.abort();
      // Remove from active requests
      activeRequests.delete(requestTrackerId);
    }

    // Set up new controller
    const controller = new AbortController();
    assign(config, { signal: controller.signal });
    activeRequests.set(requestTrackerId, controller);
  } else if (cancelGroup) {
    // Handle group cancellation
    const controller = new AbortController();
    assign(config, { signal: controller.signal });
    addAbortControllerToGroup(cancelGroup, controller);
  }

  // Add authentication and super admin headers
  const { token } = await Cognito.getAuthSession();
  const { isSuperAdmin, client: selectedClient } = getSuperAdminData();

  set(config, 'headers', {
    authorization: token ? `Bearer ${token}` : '',
    ...(isSuperAdmin && { 'x-admin-for': selectedClient }),
  });

  return config;
}, (error) => Promise.reject(error));

/**
 * Cleanup function for request groups
 */
function cleanupRequestGroup(groupKey, controller) {
  const controllers = activeRequestGroups.get(groupKey);
  if (controllers) {
    controllers.delete(controller);
    if (controllers.size === 0) {
      activeRequestGroups.delete(groupKey);
    }
  }
}

/**
 * Handle auth errors by retrying with a fresh token
 */
async function handleAuthError(error) {
  const originalRequest = error.config;
  originalRequest._retry = true;

  const { token } = await Cognito.getAuthSession({ forceRefresh: true });
  axios.defaults.headers.common.Authorization = `Bearer ${token}`;

  return client(originalRequest);
}

/**
 * Handle response lifecycle: cleanup of request tracking
 */
client.interceptors.response.use(
  (response) => {
    // Clean up request tracking
    const { requestTrackerId, cancelGroup } = response.config;

    if (requestTrackerId) {
      activeRequests.delete(requestTrackerId);
    }

    if (cancelGroup) {
      cleanupRequestGroup(cancelGroup, response.config.signal.controller);
    }

    return response;
  },
  async (error) => {
    // Clean up request tracking on error
    const { requestTrackerId, cancelGroup } = error.config || {};

    if (requestTrackerId) {
      activeRequests.delete(requestTrackerId);
    }

    if (cancelGroup) {
      cleanupRequestGroup(cancelGroup, error.config.signal.controller);
    }

    // Handle auth errors with retry
    if ((error.response?.status === 401 || error.response?.status === 403) && !error.config?._retry) {
      try {
        return await handleAuthError(error);
      } catch (authError) {
        Cognito.notifySessionInvalid();
        return Promise.reject(authError);
      }
    }

    if (!error.response) {
      return Promise.reject(new Error('Forbidden access!', { cause: { status: 403 } }));
    }

    return Promise.reject(error);
  },
);

export default client;
